import { useMemo, useState } from 'react';
import {
  useCreateIntWallMutation,
  usePatchIntWallMutation,
  useDeleteIntWallMutation,
  useGetIntWallPlanesQuery,
} from 'app/services/ahuBoardFormsApi';
import type { IAhuBoardSection, IAhuIntWallRow } from 'models';
import { ahuBoardBus } from '../../bus/ahuBoardBus';
import {
  useDebouncedCallback,
  validateNumberField,
  formatLegacyNumber,
} from '../../forms/formUtils';
import PaginatedSelect from '../../forms/PaginatedSelect';
import ItemsPager from './ItemsPager';
import { DeleteConfirmPopup } from '../SharedPopups';

/**
 * Phase 6e1 — internal walls group (§7.4.1, tab.section.internal_wall.js +
 * tmpl, markup faithful): per-plane field sets, chip pager (4 visible),
 * plane switch = destroy + recreate in the other collection preserving tag,
 * `.has-default` restore, metal-sheet paginated select per wall, delete
 * confirm ("Delete Internal Wall?"), lug recalc + canvas events
 * (`update:int_wall:on:canvas` / `remove:int_wall:from:canvas` /
 * `internal_wall:created/deleted/change`, `model:vertical:int_wall:saved`
 * fired for BOTH planes — §19.2 verbatim).
 */

interface InternalWallsGroupProps {
  section: IAhuBoardSection;
  walls: IAhuIntWallRow[];
  refetchWalls: () => void;
  open: boolean;
  onToggle: () => void;
  cid: string;
  mfgId: number;
  /** The AHU's construction class — the metal-sheet catalog is scoped to it
   * and the CMS list endpoint 400s without it (viewset.py:3045). */
  constructionClassId: number | null;
}

const InternalWallsGroup = ({
  section,
  walls,
  refetchWalls,
  open,
  onToggle,
  cid,
  mfgId,
  constructionClassId,
}: InternalWallsGroupProps) => {
  const [createWall] = useCreateIntWallMutation();
  const [patchWall] = usePatchIntWallMutation();
  const [deleteWall] = useDeleteIntWallMutation();
  const { data: planes } = useGetIntWallPlanesQuery();
  const [activeWallId, setActiveWallId] = useState<number | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<IAhuIntWallRow | null>(null);

  const sorted = useMemo(() => [...walls].sort((a, b) => a.id - b.id), [walls]);
  const ids = sorted.map((w) => w.id);
  const activeId = activeWallId != null && ids.includes(activeWallId) ? activeWallId : ids[0] ?? null;

  const recalcLugs = () =>
    ahuBoardBus.emit('tunnelLugs:recalcPlacement', {
      tunnel_id: section.tunnel_id,
      hide_from_canvas: true,
    });

  const createIntWall = () => {
    // Allowed planes from CMS (order vertical→horizontal; none → just open).
    const allowed = (planes ?? []).map((p) => String(p.value ?? p.name ?? '').toLowerCase());
    const plane: 'vertical' | 'horizontal' | null = allowed.includes('vertical')
      ? 'vertical'
      : allowed.includes('horizontal')
        ? 'horizontal'
        : null;
    if (!plane) {
      if (!open) onToggle();
      return;
    }
    const awWidth = Number(section.aw_width) || 0;
    const awLength = Number(section.aw_length) || 0;
    const awHeight = Number(section.aw_height) || 0;
    // First metal sheet id resolves the default; legacy fetches the CMS list
    // and takes the first — fetch page 1 here.
    fetch(
      `/api/v1.0/cms/${mfgId}/v1.0.0/ahu/section_internal_wall/metal_sheets/?page=1` +
        (constructionClassId != null ? `&construction_class_id=${constructionClassId}` : ''),
      {
        credentials: 'same-origin',
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
      }
    )
      .then((r) => r.json())
      .then((res: { results?: Array<{ id: number }> } | Array<{ id: number }>) => {
        const rows = Array.isArray(res) ? res : res.results ?? [];
        const metalSheetId = rows[0]?.id ?? null;
        const defaults: Record<string, unknown> =
          plane === 'vertical'
            ? {
                section_id: section.id,
                tag: '',
                length: awWidth,
                width: awWidth,
                dist_from_ups_edge_on_left_wall: 0,
                dist_from_left_wall: 0,
                angle_at_left_wall: 0,
                start_height_flom_flr: 0,
                end_height_flom_flr: awHeight,
                metal_sheet_id: metalSheetId,
              }
            : {
                section_id: section.id,
                tag: '',
                length: awLength,
                width: awWidth,
                dist_from_left_wall: 0,
                height_floor_flr_at_ups_edge: 0,
                angle_from_floor: 0,
                metal_sheet_id: metalSheetId,
              };
        return createWall({ plane, data: defaults }).unwrap();
      })
      .then((created) => {
        if (!open) onToggle();
        ahuBoardBus.emit('internal_wall:created');
        setActiveWallId(created.id);
        refetchWalls();
        recalcLugs();
        // Canvas draw arrives via the storage hook in legacy (§1.10); the
        // React port emits the draw event from fresh data.
        ahuBoardBus.emit('draw:int_wall:on:canvas', created.id, {
          ...created,
          plane,
          section_id: section.id,
        });
      })
      .catch(() => undefined);
  };

  const changeField = (wall: IAhuIntWallRow, name: string, raw: string, input?: HTMLInputElement) => {
    let value: unknown = raw;
    if (input?.dataset.type === 'number') {
      if (raw === '' && input.classList.contains('has-default')) {
        value = Number(input.dataset.default ?? 0);
        input.value = formatLegacyNumber(value, input.dataset.step ?? null);
      } else {
        const res = validateNumberField(raw, {
          min: input.dataset.min != null ? Number(input.dataset.min) : null,
          max: input.dataset.max != null ? Number(input.dataset.max) : null,
          mode: 'clamp',
          step: input.dataset.step != null ? Number(input.dataset.step) : null,
        });
        if (!res.ok) return;
        value = res.value;
        // QA5 #2 — legacy re-renders + validateNumbers() after every wall
        // save, restoring the "0.00"-style display; mirror it in place.
        input.value = formatLegacyNumber(res.value, input.dataset.step ?? null);
      }
    }
    patchWall({ plane: wall.plane, id: wall.id, data: { [name]: value } })
      .unwrap()
      .then((saved) => {
        ahuBoardBus.emit('internal_wall:change');
        // §19.2: BOTH planes fire the *vertical* event name — verbatim.
        ahuBoardBus.emit('model:vertical:int_wall:saved');
        if (name !== 'tag') {
          ahuBoardBus.emit('update:int_wall:on:canvas', {
            ...wall,
            ...saved,
            plane: wall.plane,
          });
        }
        refetchWalls();
      })
      .catch(() => undefined);
  };

  const debouncedChange = useDebouncedCallback(
    (wall: IAhuIntWallRow, name: string, raw: string, input?: HTMLInputElement) =>
      changeField(wall, name, raw, input),
    1000
  );

  // Plane switch = destroy + recreate in the other collection, preserving tag.
  const switchPlane = (wall: IAhuIntWallRow, nextPlane: 'vertical' | 'horizontal') => {
    if (nextPlane === wall.plane) return;
    ahuBoardBus.emit('remove:int_wall:from:canvas', { ...wall });
    const awWidth = Number(section.aw_width) || 0;
    const awLength = Number(section.aw_length) || 0;
    const awHeight = Number(section.aw_height) || 0;
    const defaults: Record<string, unknown> =
      nextPlane === 'vertical'
        ? {
            section_id: section.id,
            tag: wall.tag ?? '',
            length: awWidth,
            width: awWidth,
            dist_from_ups_edge_on_left_wall: 0,
            dist_from_left_wall: 0,
            angle_at_left_wall: 0,
            start_height_flom_flr: 0,
            end_height_flom_flr: awHeight,
            metal_sheet_id: wall.metal_sheet_id ?? null,
          }
        : {
            section_id: section.id,
            tag: wall.tag ?? '',
            length: awLength,
            width: awWidth,
            dist_from_left_wall: 0,
            height_floor_flr_at_ups_edge: 0,
            angle_from_floor: 0,
            metal_sheet_id: wall.metal_sheet_id ?? null,
          };
    deleteWall({ plane: wall.plane, id: wall.id })
      .unwrap()
      .then(() => createWall({ plane: nextPlane, data: defaults }).unwrap())
      .then((created) => {
        setActiveWallId(created.id);
        refetchWalls();
        ahuBoardBus.emit('draw:int_wall:on:canvas', created.id, {
          ...created,
          plane: nextPlane,
          section_id: section.id,
        });
      })
      .catch(() => undefined);
  };

  const confirmDelete = () => {
    const wall = deleteTarget;
    if (!wall) return;
    deleteWall({ plane: wall.plane, id: wall.id })
      .unwrap()
      .then(() => {
        ahuBoardBus.emit('remove:int_wall:from:canvas', { ...wall });
        ahuBoardBus.emit('internal_wall:deleted');
        recalcLugs();
        refetchWalls();
      })
      .catch(() => undefined)
      .finally(() => setDeleteTarget(null));
  };

  const planeOptions = (planes ?? []).map((p) => ({
    value: String(p.value ?? p.name ?? '').toLowerCase(),
    name: String(p.name ?? ''),
  }));

  const numInput = (
    wall: IAhuIntWallRow,
    name: string,
    opts: {
      min: string;
      max: string;
      step: string;
      className: string;
      hasDefault?: boolean;
    }
  ) => (
    <input
      type="text"
      data-type="number"
      data-min={opts.min}
      data-step={opts.step}
      data-max={opts.max}
      className={opts.className}
      data-default={opts.hasDefault ? '0' : undefined}
      defaultValue={formatLegacyNumber((wall as Record<string, unknown>)[name], opts.step)}
      name={name}
      data-id={wall.id}
      data-plane={wall.plane}
      onBlur={(e) => changeField(wall, name, e.currentTarget.value, e.currentTarget)}
      onInput={(e) =>
        debouncedChange(wall, name, e.currentTarget.value, e.currentTarget as HTMLInputElement)
      }
    />
  );

  return (
    <ul id={`internal_wall_${cid}`} className="inputs-group internal-wall">
      <button
        className=" ka-has-tooltip add-wall btn btn-add f-size-11"
        onClick={(e) => {
          e.preventDefault();
          createIntWall();
        }}
      >
        <span>+</span> Add
      </button>
      <li className="sublist-group">
        <input
          id={`int_wall_${cid}`}
          data-subtab="int_wall"
          type="checkbox"
          name="ahu_left_bar"
          checked={open}
          onChange={onToggle}
          className="sublist-ctrl-checkbox section_subtab"
        />
        <label htmlFor={`int_wall_${cid}`} className={sorted.length ? '' : 'non-clickable'}>
          Internal Wall <i className="icon-add" />
        </label>
        <div className={`accessory-wrapper accessory-wrapper_${cid}`} id="internal-wall">
          {/* QA5 #9 — chips + arrows live OUTSIDE the open-gate: legacy's
              .int_wall_count sits in the absolutely-positioned .items-wrapper
              on the header row and stays visible when the group is collapsed
              (only the form's .sublist collapses via CSS). */}
          <ItemsPager
            ids={ids}
            activeId={activeId}
            onSelect={setActiveWallId}
            className="int_wall_count items-wrapper"
          />
          {open && (
            <>
              <form className="int_wall_form" onSubmit={(e) => e.preventDefault()}>
                <ul className="sublist">
                  {sorted
                    .filter((wall) => wall.id === activeId)
                    .map((wall) => (
                      <WallFields
                        key={`${wall.plane}-${wall.id}`}
                        wall={wall}
                        planeOptions={planeOptions}
                        mfgId={mfgId}
                        constructionClassId={constructionClassId}
                        numInput={numInput}
                        onPlaneSwitch={switchPlane}
                        onTagChange={(w, v) => debouncedChange(w, 'tag', v)}
                        onDelete={setDeleteTarget}
                        onMetalSheet={(w, id) => {
                          patchWall({ plane: w.plane, id: w.id, data: { metal_sheet_id: id } })
                            .unwrap()
                            .then((saved) => {
                              ahuBoardBus.emit('model:vertical:int_wall:saved');
                              ahuBoardBus.emit('update:int_wall:on:canvas', {
                                ...w,
                                ...saved,
                                plane: w.plane,
                              });
                              refetchWalls();
                            })
                            .catch(() => undefined);
                        }}
                      />
                    ))}
                </ul>
              </form>
            </>
          )}
        </div>
      </li>
      {deleteTarget && (
        <DeleteConfirmPopup
          title="Delete Internal Wall?"
          content="If you choose to Delete, all Internal Wall Data will be lost and will not be recoverable."
          name={String(deleteTarget.tag ?? '')}
          onDelete={confirmDelete}
          onCancel={() => setDeleteTarget(null)}
        />
      )}
    </ul>
  );
};

interface WallFieldsProps {
  wall: IAhuIntWallRow;
  planeOptions: Array<{ value: string; name: string }>;
  numInput: (
    wall: IAhuIntWallRow,
    name: string,
    opts: { min: string; max: string; step: string; className: string; hasDefault?: boolean }
  ) => JSX.Element;
  onPlaneSwitch: (wall: IAhuIntWallRow, plane: 'vertical' | 'horizontal') => void;
  onTagChange: (wall: IAhuIntWallRow, value: string) => void;
  onDelete: (wall: IAhuIntWallRow) => void;
  onMetalSheet: (wall: IAhuIntWallRow, id: number | null) => void;
  mfgId: number;
  constructionClassId: number | null;
}

const WallFields = ({
  wall,
  planeOptions,
  numInput,
  onPlaneSwitch,
  onTagChange,
  onDelete,
  onMetalSheet,
  mfgId,
  constructionClassId,
}: WallFieldsProps) => {
  const isVertical = wall.plane === 'vertical';
  const fieldClass = `int_wall_field int_wall_field_${wall.plane}`;
  return (
    <>
      <li className="sub-heading" data-for={wall.id}>
        <div>
          <input
            type="text"
            name="tag"
            className={`medium-plus-input creating_field ${fieldClass}`}
            defaultValue={String(wall.tag ?? '')}
            data-id={wall.id}
            data-plane={wall.plane}
            maxLength={30}
            onInput={(e) => onTagChange(wall, e.currentTarget.value)}
          />{' '}
          <a
            data-id={wall.id}
            data-plane={wall.plane}
            className="internal-wall icon-trash int_wall_delete ka-has-tooltip"
            {...{ tooltip: 'Delete Internal Wall' }}
            onClick={() => onDelete(wall)}
          />{' '}
          <span className="label">
            <span>Tag</span>
          </span>
        </div>
      </li>
      <li data-for={wall.id}>
        <div>
          <span className="selection">{isVertical ? 'Vertical' : 'Horizontal'}</span>{' '}
          <select
            data-id={wall.id}
            name="int_wall_plane"
            className="medium-plus-input creating_field"
            value={wall.plane}
            onChange={(e) =>
              onPlaneSwitch(wall, e.currentTarget.value as 'vertical' | 'horizontal')
            }
          >
            {planeOptions.map((p) => (
              <option key={p.value} value={p.value}>
                {p.name}
              </option>
            ))}
          </select>{' '}
          <span className="label">
            <span>Plane</span>
          </span>
        </div>
      </li>
      {isVertical ? (
        <>
          <li data-for={wall.id}>
            <div className="efficiency-mech">
              <span className="unit">in</span>{' '}
              {numInput(wall, 'dist_from_ups_edge_on_left_wall', {
                min: '0',
                max: '600.00',
                step: '0.01',
                className: `text-left ${fieldClass} large-input has-default`,
                hasDefault: true,
              })}{' '}
              <span
                className="label ka-has-tooltip with-info f-size-11"
                {...{
                  tooltip:
                    'Distance of beginning point of internal wall from upstream edge of section.',
                }}
              >
                <span>Upstream dist. at left wall</span>
                <i className="icon-info" />
              </span>
            </div>
          </li>
          <li data-for={wall.id}>
            <div>
              <span className="unit">in</span>{' '}
              {numInput(wall, 'dist_from_left_wall', {
                min: '0',
                max: '600.00',
                step: '0.01',
                className: `text-left large-input ${fieldClass} has-default`,
                hasDefault: true,
              })}{' '}
              <span
                className="label ka-has-tooltip with-info f-size-11"
                {...{
                  tooltip:
                    'Distance of beginning point of internal wall, away from left wall of section.',
                }}
              >
                <span>From Left Wall</span>
                <i className="icon-info" />
              </span>
            </div>
          </li>
          <li data-for={wall.id}>
            <div className="floor-lenght">
              <span className="unit">deg</span>{' '}
              {numInput(wall, 'angle_at_left_wall', {
                min: '-360',
                max: '360',
                step: '1',
                className: `text-left ${fieldClass} medium-input has-default`,
                hasDefault: true,
              })}{' '}
              <span className="unit">in</span>{' '}
              {numInput(wall, 'length', {
                min: '0',
                max: '600',
                step: '0.01',
                className: `text-left ${fieldClass} medium-input has-default`,
                hasDefault: true,
              })}{' '}
              <span
                className="label ka-has-tooltip with-info f-size-11"
                {...{
                  tooltip:
                    'Assuming zero angle is parallel to the direction of tunnel width, a positive angle rotates the internal wall counterclockwise, a negative angle rotates the internal wall clockwise.',
                }}
              >
                <span>Angle from Left Wall</span> | <span>Length</span>
                <i className="icon-info" />
              </span>
            </div>
          </li>
          <li data-for={wall.id}>
            <div className="efficiency-mech ">
              <span className="unit">in</span>{' '}
              {numInput(wall, 'start_height_flom_flr', {
                min: '0',
                max: '600.00',
                step: '0.01',
                className: `text-left ${fieldClass} medium-input has-default`,
                hasDefault: true,
              })}{' '}
              {numInput(wall, 'end_height_flom_flr', {
                min: '0',
                max: '600.00',
                step: '0.01',
                className: `text-left ${fieldClass} medium-input has-default`,
                hasDefault: true,
              })}{' '}
              <span className="label">
                <span>Floor Height Start</span> - <span>End</span>
              </span>
            </div>
          </li>
        </>
      ) : (
        <>
          <li data-for={wall.id}>
            <div>
              <span className="unit">in</span>{' '}
              {numInput(wall, 'dist_from_left_wall', {
                min: '0',
                max: '600.00',
                step: '0.01',
                className: `${fieldClass} large-input has-default text-left`,
                hasDefault: true,
              })}{' '}
              <span className="label">
                <span>From Left Wall</span>
              </span>
            </div>
          </li>
          <li data-for={wall.id}>
            <div>
              <span className="unit">in</span>{' '}
              {numInput(wall, 'width', {
                min: '6',
                max: '600',
                step: '0.01',
                className: `text-left ${fieldClass} large-input`,
              })}{' '}
              <span className="label">Width</span>
            </div>
          </li>
          <li data-for={wall.id}>
            <div>
              <span className="unit">in</span>{' '}
              {numInput(wall, 'height_floor_flr_at_ups_edge', {
                min: '0',
                max: '600',
                step: '0.01',
                className: `text-left ${fieldClass} large-input has-default`,
                hasDefault: true,
              })}{' '}
              <span className="label">
                <span>Floor Height at Upstream Edge</span>
              </span>
            </div>
          </li>
          <li data-for={wall.id}>
            <div className="floor-lenght">
              <span className="unit">in</span>{' '}
              {numInput(wall, 'angle_from_floor', {
                min: '-60',
                max: '60',
                step: '1',
                className: `text-left ${fieldClass} medium-input has-default`,
                hasDefault: true,
              })}{' '}
              <span className="unit">in</span>{' '}
              {numInput(wall, 'length', {
                min: '6',
                max: '600',
                step: '0.01',
                className: `text-left ${fieldClass} medium-input`,
              })}{' '}
              <span className="label">
                <span>Angle from Floor</span> | <span>Length</span>
              </span>
            </div>
          </li>
        </>
      )}
      <li data-for={wall.id} data-index="0">
        <div className="single-line-with-custom-select">
          <div id={`int_wall_material_select_${wall.id}`} className="select-container">
            <PaginatedSelect
              mfgId={mfgId}
              constructionClassId={constructionClassId}
              path="ahu/section_internal_wall/metal_sheets"
              value={wall.metal_sheet_id ?? null}
              onChange={(id) => onMetalSheet(wall, id)}
              selectName="metal_sheet_id"
              selectClass="int_wall_material"
              dataId={wall.id}
              dataPlane={wall.plane}
            />
          </div>
          <span className="label">Material</span>
        </div>
      </li>
    </>
  );
};

export default InternalWallsGroup;
